"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useContext, useState, useMemo } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { AuthContext } from "@/components/auth/Auth";
import { useWaveDropsLeaderboard, WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import MyStreamWaveMyVote from "./MyStreamWaveMyVote";
import { useLayout } from "../layout/LayoutContext";
import { WaveLeaderboardLoadingBar } from "@/components/waves/leaderboard/drops/WaveLeaderboardLoadingBar";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import MyStreamWaveMyVotesReset from "./MyStreamWaveMyVotesReset";
interface MyStreamWaveMyVotesProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const MyStreamWaveMyVotes: React.FC<MyStreamWaveMyVotesProps> = t0 => {
  const $ = _c(31);
  const {
    wave,
    onDropClick
  } = t0;
  const {
    connectedProfile
  } = useContext(AuthContext);
  const [pausePolling, setPausePolling] = useState(false);
  const t1 = connectedProfile?.handle ?? null;
  let t2;
  if ($[0] !== pausePolling || $[1] !== t1 || $[2] !== wave.id) {
    t2 = {
      waveId: wave.id,
      connectedProfileHandle: t1,
      sort: WaveDropsLeaderboardSort.MY_REALTIME_VOTE,
      pausePolling
    };
    $[0] = pausePolling;
    $[1] = t1;
    $[2] = wave.id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage
  } = useWaveDropsLeaderboard(t2);
  const {
    myVotesViewStyle
  } = useLayout();
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Set();
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const [checkedDrops, setCheckedDrops] = useState(t3);
  let t4;
  if ($[5] !== checkedDrops || $[6] !== drops) {
    t4 = !!drops.length && drops.every(drop => checkedDrops.has(drop.id));
    $[5] = checkedDrops;
    $[6] = drops;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const allItemsSelected = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = dropId => {
      setCheckedDrops(prev => {
        if (prev.has(dropId)) {
          prev.delete(dropId);
        } else {
          prev.add(dropId);
        }
        return new Set(prev);
      });
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleToggleCheck = t5;
  let t6;
  if ($[9] !== allItemsSelected || $[10] !== drops) {
    t6 = () => {
      if (allItemsSelected) {
        setCheckedDrops(new Set());
      } else {
        setCheckedDrops(new Set(drops.map(_temp)));
      }
    };
    $[9] = allItemsSelected;
    $[10] = drops;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleToggleSelectAll = t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = dropId_0 => {
      setCheckedDrops(prev_0 => {
        prev_0.delete(dropId_0);
        return new Set(prev_0);
      });
    };
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const removeSelected = t7;
  let t8;
  if ($[13] !== fetchNextPage || $[14] !== hasNextPage || $[15] !== isFetching || $[16] !== isFetchingNextPage) {
    t8 = () => {
      if (hasNextPage && !isFetching && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    $[13] = fetchNextPage;
    $[14] = hasNextPage;
    $[15] = isFetching;
    $[16] = isFetchingNextPage;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const intersectionElementRef = useIntersectionObserver(t8);
  let t9;
  if ($[18] !== allItemsSelected || $[19] !== checkedDrops || $[20] !== drops || $[21] !== handleToggleSelectAll || $[22] !== intersectionElementRef || $[23] !== isFetching || $[24] !== isFetchingNextPage || $[25] !== onDropClick || $[26] !== pausePolling) {
    t9 = drops.length === 0 && !isFetching ? <div className="tw-mt-10"><p className="tw-text-iron-500 tw-text-sm tw-text-center">You haven't voted on any submissions in this wave yet.</p></div> : <div className="tw-space-y-4 tw-mt-4"><MyStreamWaveMyVotesReset haveDrops={!!drops.length} selected={checkedDrops} onToggleSelectAll={handleToggleSelectAll} allItemsSelected={allItemsSelected} removeSelected={removeSelected} setPausePolling={setPausePolling} /><div className="tw-space-y-2">{drops.map(drop_1 => <MyStreamWaveMyVote key={drop_1.id} drop={drop_1} onDropClick={onDropClick} isChecked={checkedDrops.has(drop_1.id)} onToggleCheck={handleToggleCheck} isResetting={pausePolling} />)}{isFetchingNextPage && <WaveLeaderboardLoadingBar />}<div ref={intersectionElementRef} /></div></div>;
    $[18] = allItemsSelected;
    $[19] = checkedDrops;
    $[20] = drops;
    $[21] = handleToggleSelectAll;
    $[22] = intersectionElementRef;
    $[23] = isFetching;
    $[24] = isFetchingNextPage;
    $[25] = onDropClick;
    $[26] = pausePolling;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== myVotesViewStyle || $[29] !== t9) {
    t10 = <div className="tw-space-y-4 lg:tw-space-y-6 tw-px-2 sm:tw-px-4 tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 hover:tw-scrollbar-thumb-iron-300" style={myVotesViewStyle}>{t9}</div>;
    $[28] = myVotesViewStyle;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
};
export default MyStreamWaveMyVotes;
function _temp(drop_0) {
  return drop_0.id;
}